import type {
  GridColDef,
  GridRenderEditCellParams,
} from "@mui/x-data-grid-premium";
import { Typography } from "@mui/material";
import { isUndefined } from "lodash-es";
import { DataGridTextEditCell } from "../cells/DataGridTextEditCell.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { ModifyItemProductFn } from "../useModifyItemProduct.js";
import type { DocDataGridRow, PendingCellEdit } from "../types.js";
import type { ProductColumns__ItemFragment } from "./productColumns.generated.js";

interface Options {
  headerName: string;
  canEdit: boolean;
  modifyItemProduct: ModifyItemProductFn;
}

export function createProductTitleColumn<
  T extends DocDataGrid_ItemFragment & ProductColumns__ItemFragment,
>(options: Options): GridColDef<DocDataGridRow<T>> {
  return {
    field: "productTitle",
    headerName: options.headerName,
    width: 160,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) => {
      const item = row.item;
      if (!item) return "";
      const pending = item.pendingChangeAttributes?.["productTitle"];
      return !isUndefined(pending) ? pending : (item.product?.title ?? "");
    },
    valueSetter: (value: string, row) => ({
      ...row,
      _pendingEdit: {
        apply: (itemId: string) =>
          options.modifyItemProduct(itemId, { productTitle: value }),
      },
    }),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid" || item.isItemEliminated) return null;
      if (!item.product) return null;
      return (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      );
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid" || item.isItemEliminated) return null;
      return <DataGridTextEditCell {...params} />;
    },
  };
}
